import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardHeader, 
  Alert, 
  CircularProgress, 
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack
} from '@mui/material';
import { 
  Upload, 
  Download, 
  Refresh, 
  FileUpload, 
  MonetizationOn,
  Business,
  CheckCircle,
  ErrorOutline,
  AccountBalance
} from '@mui/icons-material';
import { TreasuryDataGrid } from '../DataGrid/TreasuryDataGrid';
import { GridColDef } from '@mui/x-data-grid';

interface EquivalenciaItem {
  id: string
  'Codigo Gerencia': string
  Concepto: string
  // Generated per fiducia by the API: 'CUIF <name>' and 'DESC. <name>'. The set is
  // driven by the projects table, so it cannot be enumerated here.
  [key: string]: string
}

// The two columns the catalog has always had, kept verbatim so they keep their
// width and filtering behaviour. Everything beyond them is generated per fiducia.
const BASE_COLUMNS: GridColDef[] = [
  {
    field: 'Codigo Gerencia',
    headerName: 'Codigo Gerencia',
    width: 200,
    filterable: true
  },
  {
    field: 'Concepto',
    headerName: 'Concepto',
    width: 200,
    filterable: true,
  }
];

interface ActualizarEquivalenciasResultProps {
  initialData?: EquivalenciaItem[];
}

interface StatisticsData {
  totalProyectos: number;
  saldoDisponible: number;
  saldoCanje: number;
  saldoTotal: number;
}

export const ActualizarEquivalenciasResult: React.FC<ActualizarEquivalenciasResultProps> = ({ 
  initialData = [] 
}) => {
  // State management
  const [equivalencias, setEquivalencias] = useState<EquivalenciaItem[]>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);

  // Load initial data
  useEffect(() => {
    if (equivalencias.length === 0) {
      loadEquivalencias();
    } 
  }, []);

  const loadEquivalencias = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('obtener-equivalencias-actuales');
      if (!response.ok) throw new Error('Error al cargar equivalencias');
      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error(data?.error || 'Respuesta inesperada del servidor');
      }
      
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `equivalencia_${index}`
      }));

      setEquivalencias(transformedData);
    } catch (error) {
      console.error('Error loading equivalencias:', error);
      setAlert({ type: 'error', message: 'Error al cargar las equivalencias' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadClick = () => {
    setUploadDialog(true);
    setSelectedFile(null);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch('/actualizar-equivalencias', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Error al actualizar equivalencias');

      setAlert({ type: 'success', message: 'Equivalencias actualizadas exitosamente' });
      setUploadDialog(false);
      setSelectedFile(null);
      
      // Reload data
      loadEquivalencias();
    } catch (error) {
      console.error('Error uploading file:', error);
      setAlert({ type: 'error', message: 'Error al actualizar las equivalencias' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadExcel = () => {
    window.open('/download-equivalencias-excel', '_blank');
  };

  const handleRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      const response = await fetch('/obtener-equivalencias-actuales');
      if (!response.ok) throw new Error('Error al cargar equivalencias');
      const data = await response.json();
      
      // Transform data to ensure proper IDs
      const transformedData = data.map((item: any, index: number) => ({
        ...item,
        id: `equivalencia_${index}`
      }));

      setEquivalencias(transformedData);
      setAlert({ type: 'success', message: 'Equivalencias actualizadas exitosamente' });
    } catch (error) {
      console.error('Error loading equivalencias:', error);
      setAlert({ type: 'error', message: 'Error al cargar las equivalencias' });
    } finally {
      setIsManualRefreshing(false);
    }
  };

  // Column definitions.
  //
  // The two base columns are fixed; everything else is a CUIF/DESC. pair generated
  // per fiducia by the API, so the set is discovered from the data rather than
  // enumerated here. Order arrives correct because the API returns the base columns
  // first and then one CUIF/DESC. pair per fiducia, and JS preserves string-key
  // insertion order. That holds only while the Flask route does not sort keys --
  // jsonify does, which is why obtener_equivalencias_actuales avoids it.
  const columns: GridColDef[] = useMemo(() => {
    const baseFields = BASE_COLUMNS.map(c => c.field);

    // Union across every row, not just the first: a row missing a key would
    // otherwise hide a whole column.
    const extraFields: string[] = [];
    equivalencias.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key === 'id' || baseFields.includes(key) || extraFields.includes(key)) return;
        extraFields.push(key);
      });
    });

    return [
      ...BASE_COLUMNS,
      ...extraFields.map(field => ({
        field,
        headerName: field,
        width: 220,
        filterable: true,
        sortable: true,
      })),
    ];
  }, [equivalencias]);

  const closeAlert = () => setAlert(null);

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      {/* Alert */}
      {alert && (
        <Alert 
          severity={alert.type} 
          onClose={closeAlert}
          sx={{ mb: 2 }}
          icon={alert.type === 'success' ? <CheckCircle /> : <ErrorOutline />}
        >
          {alert.message}
        </Alert>
      )}

      {/* Main Card */}
      <Card>
        <CardHeader
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <MonetizationOn color="success" />
              <Typography variant="h5" component="div">
                Actualizar Equivalencias de Conceptos de Facturas
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={handleDownloadExcel}
                size="small"
              >
                Descargar Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={isManualRefreshing ? <CircularProgress size={16} /> : <Refresh />}
                onClick={handleRefresh}
                size="small"
                disabled={isManualRefreshing}
              >
                {isManualRefreshing ? 'Actualizando...' : 'Actualizar'}
              </Button>
            </Stack>
          }
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'white',
            '& .MuiCardHeader-action': { 
              '& .MuiButton-outlined': {
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }
              }
            }
          }}
        />
        <CardContent>
          {/* Upload Section */}
          <Paper sx={{ p: 2, mb: 2, backgroundColor: 'grey.50' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="h6" gutterBottom>
                  <Upload sx={{ mr: 1 }} />
                  Cargar archivo de Equivalencias de Conceptos de Facturas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  El archivo debe contener, como mínimo, las columnas Concepto y
                  Codigo Gerencia. Las columnas CUIF y DESC. de cada fiducia se
                  generan automáticamente: descárguelas, diligéncielas y vuelva a
                  subir el archivo.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FileUpload />}
                  onClick={handleUploadClick}
                  fullWidth
                  disabled={isLoading || isManualRefreshing}
                >
                  Actualizar Equivalencias de Conceptos de Facturas
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Data Grid */}
          <Box sx={{ mb: 2 }}>
            <TreasuryDataGrid
              rows={equivalencias}
              columns={columns}
              loading={isLoading}
              showToolbar={true}
              showExport={true}
              showRefresh={true}
              title="Equivalencias de Conceptos de Facturas"
              tableId="equivalencias-conceptos-facturas"
              onRefresh={loadEquivalencias}
              enableStickyHorizontalScroll
              exportOptions={{
                filename: 'equivalencias_conceptos_facturas',
                includeHeader: true,
                includeFooter: true,
                // Off deliberately: nothing here is numeric, and the summary
                // auto-detector keys on field names containing valor/monto/saldo --
                // a fiducia named "...VALORES..." would get a bogus total.
                includeSummary: false
              }}
              // sx={{ height: 500 }}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
              }}
              pageSizeOptions={[25, 50, 100]}
              disableRowSelectionOnClick
              density="compact"
              filterMode="client"
              sortingMode="client"
              paginationMode="client"
              checkboxSelection={false}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <FileUpload />
            Cargar Archivo de Equivalencias de Conceptos de Facturas
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Seleccionar archivo Excel (.xlsx) con las equivalencias de conceptos de facturas actualizadas
            </Typography>
            <TextField
              type="file"
              fullWidth
              variant="outlined"
              onChange={handleFileSelect}
              inputProps={{
                accept: '.xlsx,.xls',
              }}
              sx={{ mt: 2 }}
            />
            {selectedFile && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Archivo seleccionado:</strong> {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                </Typography>
              </Box>
            )}
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                El archivo debe contener, como mínimo, las columnas Concepto y
                Codigo Gerencia. Puede incluir además las columnas CUIF y DESC. de
                cada fiducia; si omite alguna, se conservan los valores ya guardados.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUploadDialog(false)}
            disabled={isUploading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            color="success"
            disabled={!selectedFile || isUploading}
            startIcon={isUploading ? <CircularProgress size={20} /> : <Upload />}
          >
            {isUploading ? 'Cargando...' : 'Cargar Archivo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActualizarEquivalenciasResult;